import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { ArrowLeft, FileText, Shield, CreditCard, AlertTriangle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';

export const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <Card className="border-slate-200">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <Scale className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle className="font-heading text-2xl">Terms and Conditions</CardTitle>
                <p className="text-sm text-muted-foreground">Last updated: March 2026</p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            <ScrollArea className="h-[70vh]">
              <div className="p-6 space-y-8 text-sm">
                
                {/* Introduction */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    1. Introduction
                  </h2>
                  <p className="text-muted-foreground leading-relaxed">
                    Welcome to FixResume24 ("we", "our", "us", or "the Service"). By accessing or using our 
                    resume refinement and template conversion services, you agree to be bound by these Terms 
                    and Conditions. Please read them carefully before using our Service.
                  </p>
                </section>

                {/* Services Provided */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">2. Services Provided</h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    FixResume24 provides the following services:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li><strong>Resume Refinement:</strong> AI-powered grammar correction, clarity improvements, and professional language enhancement</li>
                    <li><strong>Template Conversion:</strong> Converting your resume content into professional templates</li>
                    <li><strong>LinkedIn Generator:</strong> Creating optimized LinkedIn headlines and summaries</li>
                    <li><strong>Job Match Analysis:</strong> Comparing your resume against job descriptions</li>
                  </ul>
                </section>

                {/* User Responsibilities */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    3. User Responsibilities
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    By using our Service, you agree to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Provide accurate and truthful information in your resume</li>
                    <li>Review all AI-generated content before using it</li>
                    <li>Take full responsibility for the accuracy and truthfulness of your resume content</li>
                    <li>Not use our Service for any fraudulent or misleading purposes</li>
                    <li>Not upload content that infringes on third-party rights</li>
                  </ul>
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 font-medium">Important Disclaimer</p>
                    <p className="text-amber-700 text-xs mt-1">
                      FixResume24 enhances formatting and language clarity only. We do not verify the accuracy 
                      of your resume content. You are solely responsible for ensuring all information in your 
                      resume is accurate, truthful, and does not misrepresent your qualifications.
                    </p>
                  </div>
                </section>

                {/* Premium Services */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    4. Premium Services & Payments
                  </h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong>Pricing:</strong> Premium access is available at Rs.29 for 24-hour access.</p>
                    <p><strong>Access Duration:</strong> Premium features are available for exactly 24 hours from the time of purchase. After expiration, your account will revert to free tier access.</p>
                    <p><strong>Payment Processing:</strong> All payments are processed securely through Razorpay. We do not store your payment card details.</p>
                    <p><strong>No Refunds:</strong> Due to the digital nature of our services and immediate access upon payment, all sales are final. We do not offer refunds for premium purchases.</p>
                    <p><strong>No Auto-Renewal:</strong> Premium access does not auto-renew. You must manually purchase again if you wish to continue premium access.</p>
                  </div>
                </section>

                {/* Free Tier Limitations */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">5. Free Tier Limitations</h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                    <li>Limited to 2 downloads per day</li>
                    <li>Access to 11 free templates only</li>
                    <li>Downloads include a watermark</li>
                    <li>Basic refinement features only</li>
                  </ul>
                </section>

                {/* Data Privacy */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    6. Data Privacy & Security
                  </h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong>Session-Only Processing:</strong> Your resume data is processed in memory only and is automatically deleted within 2-3 hours. We do not permanently store your resume content.</p>
                    <p><strong>Account Data:</strong> We store only your email address and name (from Google login) for account identification purposes.</p>
                    <p><strong>AI Processing:</strong> Your resume content is sent to AI services (OpenAI) for processing. Please review our Privacy Policy for more details.</p>
                  </div>
                  <p className="mt-3 text-muted-foreground">
                    For complete details on how we handle your data, please read our{' '}
                    <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </p>
                </section>

                {/* Intellectual Property */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">7. Intellectual Property</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong>Your Content:</strong> You retain all rights to your original resume content. AI-generated improvements and suggestions become part of your content once generated.</p>
                    <p><strong>Our Templates:</strong> Resume templates provided by FixResume24 are licensed for personal use only. Commercial redistribution of templates is prohibited.</p>
                    <p><strong>Service Materials:</strong> All other materials, including our website design, logos, and software, are owned by FixResume24 and protected by intellectual property laws.</p>
                  </div>
                </section>

                {/* Limitation of Liability */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">8. Limitation of Liability</h2>
                  <div className="p-4 bg-slate-50 rounded-lg text-muted-foreground">
                    <p className="mb-3">
                      TO THE MAXIMUM EXTENT PERMITTED BY LAW, FIXRESUME24 SHALL NOT BE LIABLE FOR:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                      <li>Loss of profits, data, or business opportunities</li>
                      <li>Any damages arising from your use of or inability to use the Service</li>
                      <li>Any inaccuracies in AI-generated content</li>
                      <li>Employment decisions made based on resumes created using our Service</li>
                    </ul>
                    <p className="mt-3">
                      Our total liability shall not exceed the amount paid by you for premium services in the 12 months preceding the claim.
                    </p>
                  </div>
                </section>

                {/* Service Availability */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">9. Service Availability</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We strive to maintain 99.9% uptime but do not guarantee uninterrupted service. We may 
                    temporarily suspend the Service for maintenance, updates, or technical issues. We are 
                    not liable for any loss or inconvenience caused by service interruptions.
                  </p>
                </section>

                {/* Technical Requirements */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">10. Technical Requirements & Browser Compatibility</h2>
                  <div className="space-y-3 text-muted-foreground">
                    <p><strong>Recommended Browsers:</strong> For the best experience, we recommend using Safari, Google Chrome, Mozilla Firefox, or Microsoft Edge.</p>
                    <p><strong>In-App Browsers:</strong> Downloads may not function properly when using in-app browsers (such as Google app, Facebook, Instagram, or other social media app browsers). For reliable downloads, please open our website directly in Safari or Chrome.</p>
                    <p><strong>iOS Users:</strong> On iPhone/iPad, PDF files will open in your browser's viewer. To save the file, tap the Share icon and select "Save to Files".</p>
                    <p><strong>Mobile Devices:</strong> Our service is optimized for both desktop and mobile devices. Some features may have a slightly different user experience on mobile.</p>
                  </div>
                </section>

                {/* Termination */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">11. Termination</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We reserve the right to suspend or terminate your access to the Service at any time, 
                    without notice, for conduct that we believe violates these Terms or is harmful to other 
                    users, us, or third parties. You may stop using the Service at any time.
                  </p>
                </section>

                {/* Changes to Terms */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">12. Changes to Terms</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We may update these Terms from time to time. We will notify users of significant changes 
                    by posting a notice on our website. Your continued use of the Service after changes 
                    constitutes acceptance of the updated Terms.
                  </p>
                </section>

                {/* Governing Law */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">13. Governing Law</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    These Terms shall be governed by and construed in accordance with the laws of India. 
                    Any disputes arising from these Terms or your use of the Service shall be subject to 
                    the exclusive jurisdiction of the courts in Bangalore, Karnataka, India.
                  </p>
                </section>

                {/* Contact */}
                <section>
                  <h2 className="text-lg font-semibold text-primary mb-3">14. Contact Us</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    If you have any questions about these Terms, please contact us at:
                  </p>
                  <div className="mt-3 p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium text-primary">FixResume24 Support</p>
                    <p className="text-muted-foreground">Email: support@fixresume24.com</p>
                  </div>
                </section>

                {/* Acknowledgment */}
                <section className="border-t pt-6">
                  <p className="text-muted-foreground text-center">
                    By using FixResume24, you acknowledge that you have read, understood, and agree to be 
                    bound by these Terms and Conditions.
                  </p>
                </section>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TermsPage;
